import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, Receipt, ArrowBack } from '@mui/icons-material';

import Loading from '../global/loading/Loading';
import { useOrder } from '../../context/OrderContext';
import InquiryForm from '../../forms/InquiryForm';
import PaymentForm from '../../forms/order/PaymentForm';
import { DeliveryMethod } from '../../models/Order';
import Login from './login/Login';

import OrderComponent from './order/Order';
import Confirmation from './confirmation/Confirmation';
import { Offer } from '../../models/Offer';

export type CartProduct = {
  id: number;
  name: string;
  quantity: number;
  price: number;
  packaged: boolean;
  slug: string;
  image: string;
};

type CartProps = {
  products: CartProduct[];
  offer?: Offer;
  orderType?: 'standard' | 'offer';
};

function Cart({ products, offer, orderType = 'standard' }: CartProps) {
  const navigate = useNavigate();
  const { getDeliveryOptions } = useOrder();

  const [activeStep, setActiveStep] = useState<number>(1);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const handleDialogOpen = () => {
    setIsDialogOpen(true);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  const [deliveryCosts, setDeliveryCosts] = React.useState<
    { deliveryCost: number; deliveryMethod: DeliveryMethod }[] | null
  >(null);
  useEffect(() => {
    if (orderType === 'offer' && offer) {
      setDeliveryCosts([
        {
          deliveryCost: offer.deliveryPrice,
          deliveryMethod: offer.deliveryMethod,
        },
      ]);
    } else {
      const fetchDeliveryCosts = async () => {
        const costs = await getDeliveryOptions(
          products.map((product) => product.id),
        );
        setDeliveryCosts(costs);
      };

      fetchDeliveryCosts();
    }
  }, [getDeliveryOptions, offer, orderType, products]);

  if (deliveryCosts === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (deliveryCosts.length === 0) {
    return (
      <AnimatePresence>
        <motion.div
          className="my-24 rounded-lg bg-amber-50 border border-amber-200 p-4 sm:p-6 shadow-sm"
          variants={itemVariants}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Info className="h-5 w-5 text-amber-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-amber-800">
                Produkt niestandardowy
              </h3>
              <div className="mt-2 text-sm text-amber-700">
                <p>
                  Twoje zamówienie zawiera produkt, który nie może zostać
                  dostarczony standardowymi metodami wysyłki. Prosimy o kontakt
                  w celu ustalenia szczegółów dostawy.
                </p>
                <div className="flex flex-wrap gap-3 mt-4">
                  <motion.button
                    className="px-4 py-2 border border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white rounded-md text-sm font-medium transition-colors flex items-center"
                    onClick={handleDialogOpen}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Receipt className="mr-1.5" fontSize="small" />
                    Złóż zapytanie ofertowe
                  </motion.button>
                  <motion.button
                    className="px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 rounded-md text-sm font-medium transition-colors"
                    onClick={() => navigate('/')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Wróć do sklepu
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Formularz zapytania - istniejący, niemodyfikowany */}
        <InquiryForm
          isDialogOpen={isDialogOpen}
          setIsDialogOpen={setIsDialogOpen}
          products={products.map((product) => ({
            productId: product.id,
            quantity: product.quantity,
          }))}
          singleProduct={false}
          callback={() => {
            navigate('/');
          }}
        />
      </AnimatePresence>
    );
  }

  return (
    <motion.div
      className="mx-auto px-4 py-8 sm:px-6 lg:px-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/')}
            className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ArrowBack />
          </button>
          <h1 className="text-2xl font-medium text-gray-900">
            Finalizacja zakupu
          </h1>
        </div>

        {/* Kroki zamówienia */}
        <motion.div className="mb-8" variants={itemVariants}>
          <div className="w-full max-w-3xl mx-auto">
            <div className="relative">
              {/* Linia łącząca kroki */}
              <div className="absolute top-6 left-0 w-full h-1 bg-gray-200">
                <div
                  className="h-full bg-primary transition-all duration-500 ease-in-out"
                  style={{
                    width:
                      activeStep === 1
                        ? '0%'
                        : activeStep === 2
                          ? '50%'
                          : '100%',
                  }}
                />
              </div>

              {/* Kroki */}
              <ol className="relative flex justify-between w-full z-10">
                {/* Krok 1 - Zamówienie */}
                <li className="flex flex-col items-center">
                  <motion.div
                    className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                      activeStep >= 1
                        ? 'bg-primary border-primary text-white'
                        : 'bg-white border-gray-300 text-gray-500'
                    } font-semibold text-lg mb-2`}
                    whileHover={{ scale: 1.05 }}
                  >
                    1
                  </motion.div>
                  <span
                    className={`text-sm font-medium ${activeStep === 1 ? 'text-primary' : 'text-gray-700'}`}
                  >
                    Zamówienie
                  </span>
                </li>

                {/* Krok 2 - Płatność */}
                <li className="flex flex-col items-center">
                  <motion.div
                    className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                      activeStep >= 2
                        ? 'bg-primary border-primary text-white'
                        : 'bg-white border-gray-300 text-gray-500'
                    } font-semibold text-lg mb-2`}
                    whileHover={{ scale: 1.05 }}
                  >
                    2
                  </motion.div>
                  <span
                    className={`text-sm font-medium ${activeStep === 2 ? 'text-primary' : 'text-gray-700'}`}
                  >
                    Płatność
                  </span>
                </li>

                {/* Krok 3 - Potwierdzenie */}
                <li className="flex flex-col items-center">
                  <motion.div
                    className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                      activeStep >= 3
                        ? 'bg-primary border-primary text-white'
                        : 'bg-white border-gray-300 text-gray-500'
                    } font-semibold text-lg mb-2`}
                    whileHover={{ scale: 1.05 }}
                  >
                    3
                  </motion.div>
                  <span
                    className={`text-sm font-medium ${activeStep === 3 ? 'text-primary' : 'text-gray-700'}`}
                  >
                    Potwierdzenie
                  </span>
                </li>
              </ol>
            </div>
          </div>
        </motion.div>
      </motion.div>

      <Routes>
        {/* Krok 0 - Logowanie */}
        <Route path="/logowanie" element={<Login />} />
        {/* Krok 1 - Zamówienie */}
        <Route
          path="/zamówienie"
          element={
            <OrderComponent
              itemVariants={itemVariants}
              shoppingList={products}
              setActiveStep={setActiveStep}
              deliveryCosts={deliveryCosts}
              orderType={orderType}
              offer={offer}
            />
          }
        />
        {/* Krok 2 - Płatność */}
        <Route
          path="/zamówienie/:id/płatność"
          element={<PaymentForm onComplete={() => setActiveStep(3)} />}
        />
        {/* Krok 3 - Potwierdzenie */}
        <Route
          path="/potwierdzenie"
          element={<Confirmation setActiveStep={setActiveStep} />}
        />
      </Routes>
    </motion.div>
  );
}

export default Cart;
